/**
 * Spotting events the funnel probably already has.
 *
 * One flyer photographed twice, an itinerary forwarded and then pasted, a schedule that repeats
 * a fixture already on the calendar — the funnel will happily extract all of it. A duplicate
 * flag is a *hint for review*, not a gate: the user picks what to do with each one (skip it,
 * merge it into the event that already exists, or keep both), and a deliberate confirm-all
 * creates every unresolved duplicate as-is.
 *
 * Matching is deliberately loose-but-cheap: same day, similar title. Times drift between a
 * poster and the calendar entry someone typed from it, so requiring the exact instant would miss
 * the duplicates that matter, and this only ever *flags* — it never silently drops an event.
 */

import { type EventDraft, type EventTime, timeDate } from '@project/event-format';

/** An event already on the user's calendar, for the funnel to check new extractions against. */
export interface IntakeExistingEvent {
  readonly id: string;
  readonly title: string;
  readonly start: EventTime;
}

/** What an extracted form appears to duplicate. */
export interface IntakeDuplicate {
  /** `calendar` — an event that already exists; `batch` — an earlier form from this same input. */
  readonly scope: 'calendar' | 'batch';
  /** The existing event's id, or the earlier form's id. */
  readonly id: string;
  readonly title: string;
}

/**
 * What the user chose to do with a flagged duplicate. Unresolved duplicates are created as-is
 * on confirm-all — the flag informs, it doesn't decide.
 */
export type IntakeDuplicateResolution = 'skip' | 'merge' | 'keep';

/**
 * Lowercase, punctuation-free, single-spaced — so "Sam's Recital!" matches "sams recital".
 *
 * Titles arrive from a device calendar, where an untitled event can come back with no title at
 * all; a comparison is no reason to bring a screen down, so anything that isn't a string is
 * simply nothing to match on.
 */
function normalizeTitle(title: string): string {
  if (typeof title !== 'string') return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * The shortest title that counts as evidence on its own. Below this, containment is noise: "PT"
 * sits inside "Optician" and "Run" inside "Brunch", and flagging those as duplicates asks the
 * user to make a decision about two unrelated events.
 */
const MIN_CONTAINED_WORDS = 2;

/**
 * Do two titles name the same thing? Equal, or the shorter appearing in the longer as whole words
 * ("Recital" ⊂ "Sam's recital"), which a mid-word coincidence cannot fake.
 */
function titlesMatch(a: string, b: string): boolean {
  const left = normalizeTitle(a);
  const right = normalizeTitle(b);
  if (left === '' || right === '') return false;
  if (left === right) return true;

  const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
  const words = shorter.split(' ');
  // One short word ("PT", "Run") is a coincidence waiting to happen; two, or one long one, is a
  // name. Whole-word containment either way.
  if (words.length < MIN_CONTAINED_WORDS && shorter.length < 5) return false;
  return ` ${longer} `.includes(` ${shorter} `);
}

function sameDay(a: EventTime, b: EventTime): boolean {
  return timeDate(a) === timeDate(b);
}

/**
 * The first thing `draft` looks like a duplicate of — an earlier form in the same batch takes
 * precedence over the calendar, because that's the copy the user is looking at right now.
 */
export function findIntakeDuplicate(
  draft: EventDraft,
  earlier: readonly { readonly id: string; readonly draft: EventDraft }[],
  existing: readonly IntakeExistingEvent[],
): IntakeDuplicate | undefined {
  for (const form of earlier) {
    if (sameDay(draft.start, form.draft.start) && titlesMatch(draft.title, form.draft.title)) {
      return { scope: 'batch', id: form.id, title: form.draft.title };
    }
  }
  for (const event of existing) {
    if (sameDay(draft.start, event.start) && titlesMatch(draft.title, event.title)) {
      return { scope: 'calendar', id: event.id, title: event.title };
    }
  }
  return undefined;
}
